import type * as Sentry from '@sentry/angular';

/**
 * 错误信息接口
 */
export interface ErrorInfo {
  /** 额外的错误上下文信息 */
  context?: Record<string, any>;
  /** 错误消息 */
  message: string;
  /** 错误堆栈 */
  stack?: string;
  /** 发生时间 */
  timestamp: string;
  /** 错误类型 */
  type: ErrorType;
  /** 当前页面URL */
  url: string;
  /** 用户代理信息 */
  userAgent: string;
}

/**
 * 错误类型枚举
 */
export enum ErrorType {
  /** Angular框架错误 */
  ANGULAR = 'angular',
  /** JavaScript运行时错误 */
  JAVASCRIPT = 'javascript',
  /** 网络错误 */
  NETWORK = 'network',
  /** Promise未捕获的拒绝 */
  PROMISE_REJECTION = 'promise_rejection',
  /** 资源加载错误 */
  RESOURCE = 'resource',
  /** 未知错误 */
  UNKNOWN = 'unknown',
}

/**
 * 浏览器信息接口
 */
export interface BrowserInfo {
  /** 浏览器名称 */
  name: string;
  /** 操作系统 */
  os: string;
  /** 完整的用户代理字符串 */
  userAgent: string;
  /** 浏览器版本 */
  version: string;
}

/**
 * 错误处理配置接口
 */
export interface ErrorHandlerConfig {
  /** 是否在控制台输出错误 */
  enableConsoleLog?: boolean;
  /** 是否启用错误上报 */
  enableReporting?: boolean;
  /** 错误过滤函数，返回true表示处理该错误 */
  errorFilter?: (error: any) => boolean;
  /** 错误上报的API端点 */
  reportingEndpoint?: string;
  sentryOptions?: Sentry.ErrorHandlerOptions;
}

/**
 * 解析浏览器信息
 */
export function parseBrowserInfo(): BrowserInfo {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = 'Unknown';
  let os = 'Unknown';

  // 浏览器检测
  if (ua.includes('Edg/')) {
    browser = 'Microsoft Edge';
    version = ua.match(/Edg\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Chrome/')) {
    browser = 'Google Chrome';
    version = ua.match(/Chrome\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Firefox/')) {
    browser = 'Mozilla Firefox';
    version = ua.match(/Firefox\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    version = ua.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown';
  }

  // 操作系统检测
  if (ua.includes('Windows NT')) {
    const winVersion = ua.match(/Windows NT ([0-9.]+)/)?.[1];
    switch (winVersion) {
      case '10.0':
        os = 'Windows 10/11';
        break;
      case '6.1':
        os = 'Windows 7';
        break;
      case '6.2':
        os = 'Windows 8';
        break;
      case '6.3':
        os = 'Windows 8.1';
        break;
      default:
        os = `Windows NT ${winVersion}`;
    }
  } else if (ua.includes('Android')) {
    const androidVersion = ua.match(/Android ([0-9.]+)/)?.[1];
    os = `Android ${androidVersion || 'Unknown'}`;
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    const iosVersion = ua.match(/OS ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    os = `iOS ${iosVersion || 'Unknown'}`;
  } else if (ua.includes('Mac OS X')) {
    const macVersion = ua.match(/Mac OS X ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    os = `macOS ${macVersion || 'Unknown'}`;
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  }

  return {
    name: browser,
    os,
    userAgent: ua,
    version,
  };
}

/**
 * 创建错误信息对象
 */
export function createErrorInfo(error: any, type: ErrorType = ErrorType.UNKNOWN, context?: Record<string, any>): ErrorInfo {
  return {
    context,
    message: error?.message || error?.toString() || 'Unknown error',
    stack: error?.stack || '',
    timestamp: new Date().toISOString(),
    type,
    url: window.location.href,
    userAgent: navigator.userAgent,
  };
}

/**
 * 格式化错误信息用于显示
 */
export function formatErrorForDisplay(errorInfo: ErrorInfo): string {
  const browserInfo = parseBrowserInfo();
  return `
错误类型: ${errorInfo.type}
错误消息: ${errorInfo.message}
发生时间: ${new Date(errorInfo.timestamp).toLocaleString('zh-CN')}
浏览器: ${browserInfo.name} ${browserInfo.version}
操作系统: ${browserInfo.os}
页面地址: ${errorInfo.url}
  `.trim();
}

/**
 * 错误存储键名常量
 */
export const ERROR_STORAGE_KEY = 'eliboo_last_error';
